import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { EiqSelect } from 'eiq-storybook';

import { ActionType } from 'src/components/CapAction/constants';
import { CapActionContainer } from 'src/components/CapAction/CapActionContainer';
import { CAPFindingLabelDefaultValues } from 'src/components/CapFinding/constants';
import {
  CapEditTextField,
  CapEditDatePickerField,
  CapMultiButtonSwitchField,
} from 'src/components/CapField';
import { CapFindingDefaultProps, CapFindingPropTypes } from 'src/components/CapFinding/propTypes';
import { FindingSectionStatus, FindingStatus, Sections, FindingSectionStatusOption } from 'src/components/CapBox/constants';
import { notification } from 'src/components/Notification';
import { useCapFindingUpsert } from 'src/hooks/cap';
import s from 'src/components/CapFinding/CapFinding.styles';
import CapFindingMobileData from 'src/components/CapFinding/CapFindingMobileData';
import CapActionResourceModel from 'src/components/CapActionResource/CapActionResourceModel.class';

const CapFindingComponent = ({
  allExternalUsersId,
  canDonwloadAttachment,
  finding,
  isActionEditible,
  isFindingEditible,
  isResponsiblePersonEditable,
  jobStatus,
  refetchCapHistories,
  updateCapQueryCache,
  isExternalUser,
  checklist,
}) => {
  const { t } = useTranslation();

  const currentFinding = { ...finding };
  currentFinding.info = currentFinding.info || { criticality: '' };
  const [currentActions, setCurrentActions] = useState(finding.actions);
  const [currentShowingActions, setCurrentShowingActions] = useState(
    finding.actions,
  );

  const upsertCapFinding = useCapFindingUpsert();
  const [collapsed, setCollapsed] = useState(false);

  const upsertWithStateUpdate = async args => {
    try {
      const response = await upsertCapFinding({ id: finding.id, ...args });
      const newFindingData = response.data.CapUpsertFinding.finding;

      updateCapQueryCache(previous => {
        const oldFindingIndex = previous.findings.findIndex(
          f => f.id === newFindingData.id,
        );
        const newFindings = { ...previous.findings };
        newFindings[oldFindingIndex] = newFindingData;
        return newFindings;
      });
    } catch (e) {
      notification.error(t('SAVE_ERROR'));
    }
  };

  const addNewAction = (scroll) => {
    const newAction = {
      actionTaken: null,
      actionType: ActionType.Corrective,
      actualCompletionOfActionDate: null,
      attachmentIds: [],
      attachmentsMeta: [],
      dueDate: null,
      facilityContact: null,
      reviewNotes: null,
      rootCause: null,
      status: FindingStatus.Pending,
      statusNotes: null,
      scrollToAction: scroll,
      resources: [new CapActionResourceModel({})]
    };

    setCurrentShowingActions([...currentShowingActions, newAction]);
  };

  const removeAction = ({ id, index }) => {
    if (id) {
      setCurrentShowingActions(
        currentShowingActions.filter(action => action.id !== id)
      );
    } else if (index || index === 0) {
      setCurrentShowingActions(
        currentShowingActions.filter((action, index2) => index !== index2)
      );
    }
  };

  useEffect(()=>{
    if((jobStatus === 'Under cap') && (currentActions.length === 0)) {
      addNewAction(false);
    }
  }, [currentActions.length]);

  useEffect(()=>{
    currentFinding.actions = currentShowingActions;
  }, [currentShowingActions]);
  
  const capActionAddingInfo = () => {
    if (currentActions.length === 0 && isFindingEditible) {
      return (
        <s.Button clickable onClick={() =>addNewAction(true)}>
          {t('CAP_ADD_ACTION')}
        </s.Button>
      );
    }

    if (currentActions.length > 0) {
      return (
        <Fragment>
          <s.Button>{`${t('ACTION')} ${currentActions.length}`}</s.Button>
          {isFindingEditible && (
            <s.Button add clickable onClick={() =>addNewAction(true)}>
              {t('ADD')}
            </s.Button>
          )}
        </Fragment>
      );
    }

    return <s.NoActionText>{t('CAP_NO_ACTIONS')}</s.NoActionText>;
  };

  const setActionCache = (index, newAction) => {
    const newCurrentActions = [...currentActions];
    const ncaIndex = newCurrentActions.findIndex(a => a.id === newAction.id);
    if (ncaIndex !== -1) {
      newCurrentActions[ncaIndex] = newAction;
    } else {
      newCurrentActions.push(newAction);
    }
    setCurrentActions(newCurrentActions);

    const newCurrentShowingActions = [...currentShowingActions];
    newCurrentShowingActions[index] = newAction;
    setCurrentShowingActions(newCurrentShowingActions);
  };

  const actionComponents = () => currentShowingActions.map((action, index) => {
    const canEditAction = isActionEditible(action.status);
    return <CapActionContainer
      key={action.id}
      action={action}
      allExternalUsersId={allExternalUsersId}
      canDonwloadAttachment={canDonwloadAttachment}
      findingId={currentFinding.id}
      findingStatus={finding.status}
      index={index}
      isActionEditible={canEditAction}
      isFindingEditible={isFindingEditible}
      refetchCapHistories={refetchCapHistories}
      removeActionFromCollection={removeAction}
      setActionCache={newAction => {
        setActionCache(index, newAction);
      }}
      setFindingStatusToPending={() => {
        if (currentFinding.status !== FindingSectionStatus.Open) {
          upsertWithStateUpdate({ status: FindingSectionStatus.Open });
        }
      }}
    />;
  });

  const isCriticalOrMajor = ['major', 'critical'].includes(
    currentFinding.info?.criticality?.toLowerCase(),
  );
  const isNotRequiredStatus = currentFinding.status === FindingStatus.NotRequired;

  const {
    checklistQuestionIdAlias,
    checklistQuestionPosition,
    section,
    number: checklistQuestionNumber,
  } = currentFinding.info;

  const getFindingLabel = () => [section, checklistQuestionNumber].filter(c => !!c).join(' > ');

  const areFindingFieldsRequired = isFindingEditible;

  let currentFindingName = currentFinding.info.name;
  if (currentFinding.info.isWildCardQuestion && currentFinding.nonCompliance) {
    currentFindingName = currentFinding.nonCompliance;
  }

  const severityDropdown = () => {
      return <s.DivRow>
      <s.StatusDiv>
        <s.Label>{t('STATUS')}</s.Label>
        {isNotRequiredStatus && isCriticalOrMajor && (
          <s.NotRequiredAlertIcon
            name={'severity-major'}
          />
        )}
      </s.StatusDiv>
      <EiqSelect 
        hasCustomChips={true} 
        isDisabled={!(!isExternalUser && isFindingEditible)}
        onChange={(_name, option) => {
          if (isCriticalOrMajor && option?.key === FindingStatus.NotRequired) {
            notification.error(
              '[Warning] You are setting a Critical or Major finding as optional.',
            );
          }
          upsertWithStateUpdate({ status: option.key });
        }}
        options={FindingSectionStatusOption} 
        value={FindingSectionStatusOption.find(status => status.key === currentFinding.status)} 
      />
    </s.DivRow>
  }

  if (collapsed) {
    return (
      <Fragment>
        <s.FindingDiv>
          <s.LeftColumn>
            <s.LeftColumnRows>
              <s.DivRow>
                <s.Label isQuestionLabel>
                  {getFindingLabel() || CAPFindingLabelDefaultValues.CAP_FINDING_LABEL_NOT_AVAILABLE}
                </s.Label>
                <s.CollapsedRootCauseDiv>
                  <s.RootCauseText
                    collapsed={collapsed}
                    data-tip={currentFindingName}
                  >
                    {currentFindingName}
                  </s.RootCauseText>
                  <ReactTooltip place="right" />
                  <s.SeverityIcon
                    name={`severity-${currentFinding.info?.criticality?.toLowerCase()}`}
                  />
                </s.CollapsedRootCauseDiv>
              </s.DivRow>
              <s.DivRow>{capActionAddingInfo()}</s.DivRow>
            </s.LeftColumnRows>
          </s.LeftColumn>
          <s.CenterColumn>
            <s.DivRow>
              <s.Label>{t('DETAILS')}</s.Label>
              <s.Text expanded={false}>{currentFinding.detailedFinding}</s.Text>
            </s.DivRow>
          </s.CenterColumn>
          <s.RightColumn>
            <s.ExpandSwitch
              expanded={false}
              onClick={() => setCollapsed(false)}
            />
            {severityDropdown()}
          </s.RightColumn>
        </s.FindingDiv>
        {currentShowingActions.length > 0 && (
          <s.ActionsDiv>{actionComponents()}</s.ActionsDiv>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <s.FindingDiv>
        <s.LeftColumn>
          <s.LeftColumnRows>
            <s.DivRow>
              <s.Label isQuestionLabel>
                {getFindingLabel() || CAPFindingLabelDefaultValues.CAP_FINDING_LABEL_NOT_AVAILABLE}
              </s.Label>
              <s.RootCauseText
                collapsed={false}
                data-tip={currentFindingName}
              >
                {currentFindingName}
              </s.RootCauseText>
              <ReactTooltip place="right" />
            </s.DivRow>
            <s.DivRow>
              <s.Label>{t('SEVERITY')}</s.Label>
              {currentFinding.info.criticality === 'n_a' ? (
                <s.Text/>
              ) : (
                <s.SeverityTextDiv>
                  <s.SeverityIcon
                    name={`severity-${currentFinding.info?.criticality?.toLowerCase()}`}
                  />
                  <s.Text capitalize centerAligned leftMarginForSeverityIcon>
                    {currentFinding.info.criticality}
                  </s.Text>
                </s.SeverityTextDiv>
              )}
            </s.DivRow>
            <s.DivRow>
              <s.Label>{t('DUE_DATE')}</s.Label>
              <CapEditDatePickerField
                editable={isFindingEditible}
                isRequired={areFindingFieldsRequired}
                onSave={v => upsertWithStateUpdate({ dueDate: v })}
                value={currentFinding.dueDate}
              />
            </s.DivRow>
            <s.DivRow>
              <s.Label>{t('CAP_MANAGER_DASHBOARD_RESPONSIBLE_PERSON')}</s.Label>
              <CapEditTextField
                editable={isResponsiblePersonEditable}
                isRequired={areFindingFieldsRequired}
                onSave={v => upsertWithStateUpdate({ responsiblePerson: v })}
                type="text"
                value={currentFinding.responsiblePerson}
              />
            </s.DivRow>
            <s.DivRow>{capActionAddingInfo()}</s.DivRow>
          </s.LeftColumnRows>
        </s.LeftColumn>
        <s.CenterColumn>
          <s.DivRow>
            <s.Label>{t('DETAILS')}</s.Label>
            <s.Text expanded={true}>{currentFinding.detailedFinding}</s.Text>
            <CapFindingMobileData
              currentFinding={currentFinding}
            />
          </s.DivRow>
          {checklist?.currentVersion?.hasLegalRequirements && (
            <s.DivRow>
              <s.Label>{t('CAP_LEGAL_OF_COC_REQUIREMENTS')}</s.Label>
              <s.Text>{currentFinding.legalRequirements}</s.Text>
            </s.DivRow>
          )}
          {checklist?.currentVersion?.hasRecommendations && (
            <s.DivRow>
              <s.Label>{t('CAP_RECOMMENDED_ACTION')}</s.Label>
              <s.Text>{currentFinding.recommendations}</s.Text>
            </s.DivRow>
          )}
          <s.DivRow>
            <s.Label>{t('CAP_ROOT_CAUSE')}</s.Label>
            <CapEditTextField
              editable={isFindingEditible}
              isRequired={areFindingFieldsRequired}
              isTooltipDisabled={true}
              onSave={v => upsertWithStateUpdate({ rootCause: v })}
              type="textarea"
              value={currentFinding.rootCause}
            />
          </s.DivRow>
        </s.CenterColumn>
        <s.RightColumn>
          <s.ExpandSwitch
            expanded={true}
            onClick={() => setCollapsed(!collapsed)}
          />
          {severityDropdown()}
          <s.DivRow>
            <s.Label longLabel>{t('CAP_EVIDENCE_REVIEW_REQUIRED')}</s.Label>
            <s.Checkbox
              disabled={isExternalUser || !isFindingEditible}
              id={`finding-${finding.id}`}
              onChange={(v) => upsertWithStateUpdate({ evidenceReviewRequired: v.target.checked })}
              value={finding.evidenceReviewRequired}
            />
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_NOTES')}</s.Label>
            <CapEditTextField
              editable={!isExternalUser && isFindingEditible}
              isTooltipDisabled={true}
              onSave={v => upsertWithStateUpdate({ statusNotes: v })}
              type="textarea"
              value={currentFinding.statusNotes}
            />
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_DATE')}</s.Label>
            <s.Text>
              {currentFinding.statusDate && moment(new Date(currentFinding.statusDate)).format('MMMM DD, YYYY')}
            </s.Text>
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_BY')}</s.Label>
            <s.Text>
              {currentFinding.statusUpdatedByUser &&
              `${currentFinding.statusUpdatedByUser.lastName} ${currentFinding.statusUpdatedByUser.firstName}`}
            </s.Text>
          </s.DivRow>
        </s.RightColumn>
      </s.FindingDiv>
      {currentShowingActions.length > 0 && (
        <s.ActionsDiv>{actionComponents()}</s.ActionsDiv>
      )}
    </Fragment>
  );
};

CapFindingComponent.propTypes = CapFindingPropTypes;
CapFindingComponent.defaultProps = CapFindingDefaultProps;

export const CapFinding = React.memo(CapFindingComponent);
